import { useState, useEffect } from "react";
import "./Settings.css";

const THEME_OPTIONS = {
  light: {
    name: "Light Mode",
    icon: "☀️",
    description: "Clean and bright interface"
  },
  dark: {
    name: "Dark Mode",
    icon: "🌙",
    description: "Easy on the eyes in low light"
  },
  ocean: {
    name: "Ocean Blue",
    icon: "🌊",
    description: "Calm ocean-inspired colors"
  },
  forest: {
    name: "Forest Green",
    icon: "🌲",
    description: "Natural green and earth tones"
  },
  sunset: {
    name: "Sunset",
    icon: "🌅",
    description: "Warm orange and red hues"
  },
  midnight: {
    name: "Midnight",
    icon: "🌃",
    description: "Deep blue and purple tones"
  }
};

const DEFAULT_SETTINGS = {
  theme: "light",
  fontSize: "medium",
  animationsEnabled: true,
  compactMode: false,
  notificationsEnabled: true,
  autoSave: true,
  showWeekends: true,
  defaultView: "dashboard",
  entriesPerPage: 10,
  language: "English"
};

export default function Settings() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savedMessage, setSavedMessage] = useState("");
  const [activeTab, setActiveTab] = useState("appearance");

  useEffect(() => {
    const savedSettings = localStorage.getItem("appSettings");
    if (savedSettings) {
      setSettings(JSON.parse(savedSettings));
    }
  }, []);

  const handleThemeChange = (theme) => {
    setSettings({ ...settings, theme });
    applyTheme(theme);
    showSavedMessage();
  };

  const handleSettingChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
    showSavedMessage();
  };

  const handleSaveSettings = () => {
    localStorage.setItem("appSettings", JSON.stringify(settings));
    applyTheme(settings.theme);
    showSavedMessage();
  };

  const handleResetSettings = () => {
    if (window.confirm("Reset all settings to default?")) {
      setSettings(DEFAULT_SETTINGS);
      localStorage.setItem("appSettings", JSON.stringify(DEFAULT_SETTINGS));
      applyTheme(DEFAULT_SETTINGS.theme);
      showSavedMessage("Settings reset to default");
    }
  };

  const showSavedMessage = (msg = "Settings saved successfully!") => {
    setSavedMessage(msg);
    setTimeout(() => setSavedMessage(""), 3000);
  };

  const applyTheme = (theme) => {
    document.documentElement.setAttribute("data-theme", theme);
  };

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h2>⚙️ Settings</h2>
        <p className="settings-description">Customize your SelfPivot experience</p>
      </div>

      {savedMessage && <div className="saved-message">✓ {savedMessage}</div>}

      <div className="settings-tabs">
        <button
          className={`tab-btn ${activeTab === "appearance" ? "active" : ""}`}
          onClick={() => setActiveTab("appearance")}
        >
          🎨 Appearance
        </button>
        <button
          className={`tab-btn ${activeTab === "preferences" ? "active" : ""}`}
          onClick={() => setActiveTab("preferences")}
        >
          ⚡ Preferences
        </button>
        <button
          className={`tab-btn ${activeTab === "general" ? "active" : ""}`}
          onClick={() => setActiveTab("general")}
        >
          🔧 General
        </button>
      </div>

      {activeTab === "appearance" && (
        <div className="settings-section">
          <h3>🎨 Theme Selection</h3>
          <div className="theme-grid">
            {Object.entries(THEME_OPTIONS).map(([key, theme]) => (
              <div
                key={key}
                className={`theme-card ${settings.theme === key ? "active" : ""}`}
                onClick={() => handleThemeChange(key)}
              >
                <div className="theme-icon">{theme.icon}</div>
                <div className="theme-name">{theme.name}</div>
                <div className="theme-description">{theme.description}</div>
                {settings.theme === key && <div className="theme-checkmark">✓</div>}
              </div>
            ))}
          </div>

          <div className="divider"></div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Font Size</span>
              <span className="setting-description">Adjust text size for readability</span>
            </div>
            <div className="font-size-options">
              {["small", "medium", "large"].map((size) => (
                <button
                  key={size}
                  className={`font-size-btn ${settings.fontSize === size ? "active" : ""}`}
                  onClick={() => handleSettingChange("fontSize", size)}
                >
                  {size === "small" ? "A" : size === "medium" ? "A" : "A"}
                </button>
              ))}
            </div>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Animations</span>
              <span className="setting-description">Enable smooth transitions and animations</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.animationsEnabled}
                onChange={(e) => handleSettingChange("animationsEnabled", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Compact Mode</span>
              <span className="setting-description">Reduce spacing for more content</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.compactMode}
                onChange={(e) => handleSettingChange("compactMode", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      )}

      {activeTab === "preferences" && (
        <div className="settings-section">
          <h3>⚡ Your Preferences</h3>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Enable Notifications</span>
              <span className="setting-description">Get alerts for reminders and updates</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.notificationsEnabled}
                onChange={(e) => handleSettingChange("notificationsEnabled", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Auto-Save Entries</span>
              <span className="setting-description">Automatically save your progress</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.autoSave}
                onChange={(e) => handleSettingChange("autoSave", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Show Weekends</span>
              <span className="setting-description">Display weekend days in charts</span>
            </div>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={settings.showWeekends}
                onChange={(e) => handleSettingChange("showWeekends", e.target.checked)}
              />
              <span className="toggle-slider"></span>
            </label>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Default View</span>
              <span className="setting-description">Landing page after login</span>
            </div>
            <select
              value={settings.defaultView}
              onChange={(e) => handleSettingChange("defaultView", e.target.value)}
              className="select-input"
            >
              <option value="dashboard">Dashboard</option>
              <option value="analytics">Analytics</option>
              <option value="daily">Daily Log</option>
              <option value="weekly">Weekly Review</option>
            </select>
          </div>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Entries Per Page</span>
              <span className="setting-description">How many items to display</span>
            </div>
            <select
              value={settings.entriesPerPage}
              onChange={(e) => handleSettingChange("entriesPerPage", parseInt(e.target.value))}
              className="select-input"
            >
              <option value="5">5 Entries</option>
              <option value="10">10 Entries</option>
              <option value="20">20 Entries</option>
              <option value="50">50 Entries</option>
            </select>
          </div>
        </div>
      )}

      {activeTab === "general" && (
        <div className="settings-section">
          <h3>🔧 General Settings</h3>

          <div className="setting-item">
            <div className="setting-label">
              <span className="setting-title">Language</span>
              <span className="setting-description">Choose your preferred language</span>
            </div>
            <select
              value={settings.language}
              onChange={(e) => handleSettingChange("language", e.target.value)}
              className="select-input"
            >
              <option value="English">English</option>
              <option value="Spanish">Spanish</option>
              <option value="French">French</option>
              <option value="German">German</option>
              <option value="Japanese">Japanese</option>
            </select>
          </div>

          <div className="divider"></div>

          <div className="info-section">
            <h4>📱 Application Info</h4>
            <div className="info-item">
              <span className="info-label">App Name:</span>
              <span className="info-value">SelfPivot Progress Tracker</span>
            </div>
            <div className="info-item">
              <span className="info-label">Version:</span>
              <span className="info-value">2.0.0</span>
            </div>
            <div className="info-item">
              <span className="info-label">Build:</span>
              <span className="info-value">2026.03.06</span>
            </div>
          </div>

          <div className="divider"></div>

          <div className="button-group">
            <button className="save-btn" onClick={handleSaveSettings}>
              💾 Save All Settings
            </button>
            <button className="reset-btn" onClick={handleResetSettings}>
              🔄 Reset to Default
            </button>
          </div>

          <div className="divider"></div>

          <div className="help-section">
            <h4>❓ Need Help?</h4>
            <ul className="help-list">
              <li>📖 <a href="#help">View Documentation</a></li>
              <li>🐛 <a href="#bug">Report a Bug</a></li>
              <li>💡 <a href="#feedback">Send Feedback</a></li>
              <li>⚖️ <a href="#privacy">Privacy Policy</a></li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
